import { OrderStrategiesEnum } from '@src/tokens/orderStrategy';
import {
  AlgoConfigurationParameterEnum,
  bigMax,
  bigMin,
  DecisionStatusEnum,
  includesEnumValue,
  type IOrderConfigurationSnapshot,
  type OrderAnalytic,
} from '@talos/kyoko';
import Big from 'big.js';
import type { Millis, TrajectoryOrder } from './trajectoryOrder';
import { doesSnapshotConfigHave, getSnapshotConfigValue } from './util';

const SUPPORTED_TWAP_STRATEGIES = [
  OrderStrategiesEnum.MarketTWAP,
  OrderStrategiesEnum.TWAP,
  OrderStrategiesEnum.Quantizer,
  OrderStrategiesEnum.MarketQuantizer,
] as const;

const REQ_CONFIG_SNAPSHOT_KEYS = [
  AlgoConfigurationParameterEnum.MaxPercentOfOrderBehind,
  AlgoConfigurationParameterEnum.MaxSecondsBehindSchedule,
  AlgoConfigurationParameterEnum.MaxPercentOfOrderAhead,
  AlgoConfigurationParameterEnum.MaxSecondsAheadOfSchedule,
  AlgoConfigurationParameterEnum.CleanUpPeriod,
  AlgoConfigurationParameterEnum.ClipBasedResidualQtyThreshold,
];

// We cannot accuratly calculate trajectory predictions on modified or paused orders
export function shouldInvalidateTwapBounds(orderQty: Big, analytics: OrderAnalytic[]): boolean {
  const expectedQty = orderQty.toNumber();
  return !!analytics.find(
    a =>
      a.DecisionStatus === DecisionStatusEnum.Paused ||
      (a.OrderQty !== undefined && parseFloat(a.OrderQty) !== expectedQty)
  );
}

function calcClips(order: TrajectoryOrder, epsilon: Big): { clips: Big; residual: Big } {
  // If no clip size/interval, assume only one clip (ex: in a traditional TWAP)
  if (!order.clipSize || !order.clipInterval) {
    return { clips: Big(1), residual: Big(0) };
  }

  // How many clips are req for given order qty, clip size
  const reqClips = order.orderQty.div(order.clipSize).round(0, Big.roundUp);

  // If no timing contraints return number of required clips
  if (!order.endTime) {
    return { clips: reqClips, residual: Big(0) };
  }

  // How many clips can we fit in allotted time?
  const maxClips = Big((order.endTime - order.startTime) / order.clipInterval).round(0);

  // If we can fit all required clips in alloted time, return required clips
  if (reqClips.lte(maxClips)) {
    return { clips: reqClips, residual: Big(0) };
  }

  // We can't fit all required clips, figure out how many we can
  const schedulableQty = maxClips.times(order.clipSize);
  const residual = order.orderQty.minus(schedulableQty);

  // If residual < clip width * epsilon, bundle it into final clip
  if (residual.lt(order.clipSize.mul(epsilon))) {
    return { clips: maxClips, residual: Big(0) };
  }

  return { clips: maxClips, residual: residual };
}

function getBoundWidth(
  qty: Big,
  duration: number,
  configSnapshot: IOrderConfigurationSnapshot
): { lower: Big; upper: Big } {
  const calculate = (pctKey: AlgoConfigurationParameterEnum, secondsKey: AlgoConfigurationParameterEnum) => {
    const pct = getSnapshotConfigValue(configSnapshot, pctKey);
    const seconds = getSnapshotConfigValue(configSnapshot, secondsKey);
    const timeQty = (qty.toNumber() / duration) * seconds.toNumber();
    const pctQty = qty.mul(pct);
    return bigMin(pctQty, Big(timeQty));
  };

  return {
    lower: calculate(
      AlgoConfigurationParameterEnum.MaxPercentOfOrderBehind,
      AlgoConfigurationParameterEnum.MaxSecondsBehindSchedule
    ),
    upper: calculate(
      AlgoConfigurationParameterEnum.MaxPercentOfOrderAhead,
      AlgoConfigurationParameterEnum.MaxSecondsAheadOfSchedule
    ),
  };
}

function calcEndTime(order: TrajectoryOrder, clips: Big): Millis {
  let endTime = order.endTime;

  if (order.clipInterval) {
    const unboundedEndTime = order.startTime + clips.toNumber() * order.clipInterval;
    // If no end time specified, or we have a valid unbounded end time that's sooner (calculated end time based on clip interval)
    // Update the user-specified end time value to that
    if (!endTime || unboundedEndTime < endTime) {
      endTime = unboundedEndTime;
    }
  }

  if (!endTime) {
    throw new Error('Failed to get a valid order end time');
  }
  return endTime;
}

export function canPredTwapBounds(order: TrajectoryOrder, configSnapshot: IOrderConfigurationSnapshot): boolean {
  return (
    !!order.strategy &&
    includesEnumValue(SUPPORTED_TWAP_STRATEGIES, order.strategy) &&
    doesSnapshotConfigHave(configSnapshot, ...REQ_CONFIG_SNAPSHOT_KEYS)
  );
}

export interface TWAPBoundsPoint {
  ts: Millis;
  target: number;
  lb: number;
  ub: number;
}

export function genTwapBounds(
  order: TrajectoryOrder,
  configSnapshot: IOrderConfigurationSnapshot
): { schedule?: TWAPBoundsPoint[]; residual: number } {
  if (!canPredTwapBounds(order, configSnapshot)) {
    return { schedule: undefined, residual: 0 };
  }

  const clipEpsilon = getSnapshotConfigValue(
    configSnapshot,
    AlgoConfigurationParameterEnum.ClipBasedResidualQtyThreshold
  );
  const { clips, residual } = calcClips(order, clipEpsilon);

  const execQty = order.orderQty.sub(residual);

  const clipSize = order.clipSize ?? order.orderQty;
  const cleanUpPeriod = getSnapshotConfigValue(configSnapshot, AlgoConfigurationParameterEnum.CleanUpPeriod).toNumber();
  const endTime = calcEndTime(order, clips);

  const totalSteps = Math.ceil((endTime - order.startTime) / 1000);

  const fullSteps = Math.ceil((order.clipInterval ?? 0) / 1000);
  const stepsLast = totalSteps - fullSteps * clips.sub(1).toNumber();
  const stepsPerClip: number[] = [...Array(clips.sub(1).toNumber()).fill(fullSteps), stepsLast];

  const clipQtys: Big[] = [...Array(clips.toNumber() - 1).fill(clipSize), execQty.sub(clips.sub(1).mul(clipSize))];

  const schedule: TWAPBoundsPoint[] = [];
  let cumQty = Big(0);
  let ts = order.startTime;

  for (let clipIdx = 0; clipIdx < clips.toNumber(); clipIdx++) {
    const nSteps = stepsPerClip[clipIdx];
    const qClip = clipQtys[clipIdx];

    const cleanupSteps = Math.min(cleanUpPeriod / 1000, nSteps);
    const fillSteps = nSteps - cleanupSteps;

    const { lower: lbWidth, upper: ubWidth } = getBoundWidth(qClip, fillSteps, configSnapshot);

    for (let step = 0; step < nSteps; step++) {
      const progress = Math.min(step / fillSteps, 1);
      const qtyTarget = cumQty.add(qClip.mul(progress));

      const clipStartTarget = clipSize.mul(clipIdx);
      const clipEndTarget = clipIdx === clips.sub(1).toNumber() ? execQty : clipSize.mul(clipIdx + 1);
      const clipSpan = clipEndTarget.sub(clipStartTarget);
      const clipCompletedPct = qtyTarget.sub(clipStartTarget).div(clipSpan);

      const pctClamp = bigMax(Big(0), bigMin(Big(1), clipCompletedPct));
      const lb = qtyTarget.sub(lbWidth.mul(Big(1).sub(pctClamp)));
      const ub = bigMin(qtyTarget.add(ubWidth), clipEndTarget);

      schedule.push({
        ts,
        target: qtyTarget.toNumber(),
        lb: lb.toNumber(),
        ub: ub.toNumber(),
      });

      ts += 1000;
    }
    cumQty = cumQty.add(qClip);
  }

  return { schedule, residual: residual.toNumber() };
}
